import React, { useEffect, useState } from 'react';
import {
  Typography,
  CircularProgress,
  Alert as MuiAlert,
  Chip,
  Box,
  Paper,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { StatsCard } from '../components/layout/EnhancedCard';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../store/store'; // RootState removed as it's unused here
import GameLeagueSelector from '../components/GameLeagueSelector';
import { selectGameFilter, selectLeagueFilter } from '../store/slices/filterSlice';
import {
  selectAllAlerts,
  selectAlertsStatus,
  selectAlertsError,
  selectAlertsLastFetchTime,
  clearAlertsError,
  selectNewAlertIds,
  clearNewAlerts,
} from '../store/slices/alertsSlice';
import AlertsContainer from '../components/alerts/AlertsContainer';
import { useAlertsAutoRefresh } from '../utils/useAlertsAutoRefresh';
// Icons for different alert types and components
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import RefreshIcon from '@mui/icons-material/Refresh';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material/styles';
import { AlertSettingsPanel } from '../components/alerts/AlertSettingsPanel';
import AlertMetricsDialog from '../components/alerts/AlertMetricsDialog';
import BarChartIcon from '@mui/icons-material/BarChart';

const AlertsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const alerts = useSelector(selectAllAlerts);
  const status = useSelector(selectAlertsStatus);
  const error = useSelector(selectAlertsError);
  const lastFetchTime = useSelector(selectAlertsLastFetchTime);
  const newAlertIds = useSelector(selectNewAlertIds);

  // Get selected game and league from filter state
  const selectedGame = useSelector(selectGameFilter);
  const selectedLeague = useSelector(selectLeagueFilter);

  // Local state for expanded cards and filters
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [groupByHostname, setGroupByHostname] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);

  // Use auto-refresh hook with unique ID for alerts page
  const { forceRefreshAlerts, triggerRefresh, isLoading } = useAlertsAutoRefresh(true, 'alerts-page');

  useEffect(() => {
    // Only fetch alerts if we have a league selected (to avoid fetching all alerts on initial load)
    if (selectedLeague) {
      console.log(`Fetching alerts for game: ${selectedGame}, league: ${selectedLeague}`);
      forceRefreshAlerts();
    }
  }, [selectedGame, selectedLeague, forceRefreshAlerts]); // Re-fetch when filters change

  useEffect(() => {
    // Clear new alert indicators when user views the alerts page
    if (newAlertIds.length > 0) {
      const timer = setTimeout(() => {
        dispatch(clearNewAlerts());
      }, 3000); // Clear after 3 seconds of viewing the page

      return () => clearTimeout(timer);
    }
  }, [newAlertIds, dispatch]);

  useEffect(() => {
    // Clear error if it exists and user navigates away and back, or on new data load
    if (error && status !== 'failed') {
      dispatch(clearAlertsError());
    }
  }, [dispatch, error, status]);

  // Toggle card expansion
  const handleToggleExpand = (alertId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  // Group alerts by severity for statistics
  const alertStats = alerts.reduce(
    (acc, alert) => {
      const severity = alert.severity.toLowerCase();
      acc[severity] = (acc[severity] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const hasLoadedAlertsOnce = lastFetchTime !== null;
  const shouldShowLoadingState = !selectedLeague || (!hasLoadedAlertsOnce && (status === 'idle' || status === 'loading'));
  const shouldShowAlertsContent = hasLoadedAlertsOnce && (status === 'succeeded' || status === 'loading');

  let content;

  if (shouldShowLoadingState) {
    content = (
      <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' minHeight='300px' gap={2}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant='h6' color='text.secondary'>
          {!selectedLeague ? 'Waiting for league selection...' : 'Loading alerts...'}
        </Typography>
        {!selectedLeague && (
          <Typography variant='body2' color='text.secondary'>
            Please select a league to view alerts
          </Typography>
        )}
      </Box>
    );
  } else if (shouldShowAlertsContent) {
    if (alerts.length === 0) {
      content = (
        <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' minHeight='300px' textAlign='center' gap={2}>
          <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.5 }} />
          <Typography variant='h5' color='text.secondary'>
            No alerts found
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            {selectedLeague
              ? `All systems are running smoothly in ${selectedLeague}! Check back later for any new notifications.`
              : 'Please select a league to view alerts.'}
          </Typography>
        </Box>
      );
    } else {
      content = (
        <Box>
          {/* Alert Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatsCard title='CRITICAL' value={alertStats.critical || 0} icon={<ErrorOutlineIcon />} color='#E31B23' />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatsCard title='HIGH' value={alertStats.high || 0} icon={<WarningAmberIcon />} color='#F39C12' />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatsCard title='MEDIUM' value={alertStats.medium || 0} icon={<InfoOutlinedIcon />} color='#3498DB' />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatsCard title='LOW' value={alertStats.low || 0} icon={<CheckCircleOutlineIcon />} color='#27AE60' />
            </Grid>
          </Grid>

          {/* Search Bar and Group By Controls */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems='center'>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  placeholder='Search alerts by title, description, hostname, severity...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={groupByHostname}
                      onChange={e => setGroupByHostname(e.target.checked)}
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label='Group by Hostname'
                  sx={{ ml: 1 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Severity Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 500 }}>
              Filter by Severity:
            </Typography>
            <Stack direction='row' spacing={1} flexWrap='wrap'>
              {[
                { key: 'all', label: 'All Alerts', count: alerts.length },
                {
                  key: 'critical',
                  label: 'Critical',
                  count: alertStats.critical || 0,
                },
                { key: 'high', label: 'High', count: alertStats.high || 0 },
                {
                  key: 'medium',
                  label: 'Medium',
                  count: alertStats.medium || 0,
                },
                { key: 'low', label: 'Low', count: alertStats.low || 0 },
              ].map(({ key, label, count }) => (
                <Chip
                  key={key}
                  label={`${label} (${count})`}
                  variant={severityFilter === key ? 'filled' : 'outlined'}
                  color={severityFilter === key ? 'primary' : 'default'}
                  onClick={() => setSeverityFilter(key)}
                  sx={{
                    transition: 'all 0.2s ease-out',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Alert Cards Container with Animations */}
          <AlertsContainer
            severityFilter={severityFilter}
            expandedCards={expandedCards}
            onToggleExpand={handleToggleExpand}
            groupByHostname={groupByHostname}
            searchQuery={searchQuery}
          />

          {alerts.filter(alert => severityFilter === 'all' || alert.severity.toLowerCase() === severityFilter).length === 0 &&
            severityFilter !== 'all' && (
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
                minHeight='200px'
                textAlign='center'
                gap={2}
              >
                <InfoOutlinedIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5 }} />
                <Typography variant='h6' color='text.secondary'>
                  No {severityFilter} severity alerts found
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Try selecting a different severity level or clear the filter.
                </Typography>
              </Box>
            )}
        </Box>
      );
    }
  } else if (status === 'failed') {
    content = (
      <Box textAlign='center' py={4}>
        <MuiAlert
          severity='error'
          sx={{
            mb: 2,
            background: 'linear-gradient(135deg, rgba(227, 27, 35, 0.1), rgba(227, 27, 35, 0.05))',
            border: `1px solid ${alpha('#E31B23', 0.3)}`,
          }}
        >
          <Typography variant='h6' sx={{ mb: 1 }}>
            Failed to load alerts
          </Typography>
          <Typography variant='body2'>{typeof error === 'string' ? error : JSON.stringify(error)}</Typography>
        </MuiAlert>
      </Box>
    );
  }

  // Handlers for game and league changes
  const handleGameChange = (gameCode: string) => {
    console.log(`Game changed to: ${gameCode}`);
    // The actual filtering will happen via the useEffect dependency on selectedGame
  };

  const handleLeagueChange = (leagueCode: string) => {
    console.log(`League changed to: ${leagueCode}`);
    // The actual filtering will happen via the useEffect dependency on selectedLeague
  };

  return (
    <PageLayout>
      <PageHeader
        icon={<NotificationImportantIcon sx={{ fontSize: 28 }} />}
        title='Alert Center'
        subtitle='Monitor and manage all system notifications • Auto-refreshes every 10 seconds'
        actions={
          <Box display='flex' alignItems='center' gap={1}>
            <Tooltip title='Alert metrics history'>
              <IconButton
                onClick={() => setMetricsDialogOpen(true)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.main', color: 'white' },
                }}
              >
                <BarChartIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Sound notifications enabled for new critical alerts'>
              <VolumeUpIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            </Tooltip>
            <Tooltip title='Refresh alerts now'>
              <IconButton
                onClick={triggerRefresh}
                disabled={isLoading}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.main', color: 'white' },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 20,
                    animation: isLoading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title='Alert notification settings'>
              <IconButton
                onClick={() => setSettingsModalOpen(true)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.main', color: 'white' },
                }}
              >
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      <ContentSection title='Filter Options' sx={{ mb: 3 }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.8) 0%, rgba(45, 45, 45, 0.8) 100%)',
            border: '1px solid rgba(227, 27, 35, 0.1)',
            borderRadius: 2,
          }}
        >
          <GameLeagueSelector onGameChange={handleGameChange} onLeagueChange={handleLeagueChange} />
        </Paper>
      </ContentSection>

      {content}

      {/* Settings Modal */}
      <Dialog open={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Alert Notification Settings</DialogTitle>
        <DialogContent>
          <AlertSettingsPanel onClose={() => setSettingsModalOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Alert Metrics Dialog */}
      <AlertMetricsDialog open={metricsDialogOpen} onClose={() => setMetricsDialogOpen(false)} />
    </PageLayout>
  );
};

export default AlertsPage;
